<template>
  <div class="user-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span class="card-title">用户管理</span>
          <div class="header-actions">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索姓名、用户名、部门"
              style="width: 300px; margin-right: 16px"
              clearable
              @clear="handleSearch"
              @keyup.enter="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="handleAdd">
              <el-icon><Plus /></el-icon>
              新增用户
            </el-button>
            <el-button @click="openBatchDialog" style="margin-left: 8px">
              批量开通
            </el-button>
          </div>
        </div>
      </template>

      <!-- 筛选栏 -->
      <div class="filter-section">
        <el-radio-group v-model="filterActive" @change="handleSearch">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="true">启用</el-radio-button>
          <el-radio-button label="false">禁用</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        stripe
        style="width: 100%"
      >
        <el-table-column prop="user_id" label="ID" width="80" />
        <el-table-column prop="full_name" label="姓名" width="120" />
        <el-table-column prop="username" label="登录用户名" width="140" />
        <el-table-column prop="gender" label="性别" width="80">
          <template #default="{ row }">
            <span v-if="row.gender === 'male'">男</span>
            <span v-else-if="row.gender === 'female'">女</span>
            <span v-else-if="row.gender === 'other'">其他</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="130" />
        <el-table-column prop="email" label="邮箱" width="180" />
        <el-table-column prop="department" label="部门" width="120" />
        <el-table-column prop="position" label="职务" width="120" />
        <el-table-column label="角色/职务类别" min-width="180">
          <template #default="{ row }">
            <el-tag
              v-for="r in (row._roles || [])"
              :key="r.role_id"
              size="small"
              style="margin-right: 6px; margin-bottom: 4px"
            >
              {{ r.role_name }}
            </el-tag>
            <span v-if="!(row._roles || []).length">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="is_active" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'danger'">
              {{ row.is_active ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="is_admin" label="管理员" width="100">
          <template #default="{ row }">
            <el-tag v-if="row.is_admin" type="warning">是</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="must_change_password" label="首次改密" width="110">
          <template #default="{ row }">
            <el-tag v-if="row.must_change_password" type="warning">是</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="last_login_at" label="最后登录" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.last_login_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="360" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button
              size="small"
              :type="row.is_active ? 'danger' : 'success'"
              @click="handleToggleStatus(row)"
            >
              {{ row.is_active ? '禁用' : '启用' }}
            </el-button>
            <el-button
              size="small"
              type="warning"
              @click="handleResetPassword(row)"
            >
              {{ row.user_id === currentUserId ? '修改密码' : '重置密码' }}
            </el-button>
            <el-button
              size="small"
              @click="copyNotifyTemplate(row)"
            >
              复制通知
            </el-button>
            <el-button
              size="small"
              type="info"
              @click="handleManageSessions(row)"
            >
              在线设备
            </el-button>
            <el-button
              size="small"
              type="danger"
              @click="handleDelete(row)"
              :disabled="row.user_id === currentUserId"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadData"
          @current-change="loadData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="showDialog"
      width="600px"
      @closed="resetForm"
    >
      <el-form
        :model="formData"
        :rules="formRules"
        ref="formRef"
        label-width="100px"
      >
        <el-form-item label="登录用户名" prop="username">
          <el-input v-model="formData.username" placeholder="请输入登录用户名（不是姓名）" />
        </el-form-item>
        <el-form-item label="姓名" prop="full_name">
          <el-input v-model="formData.full_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="formData.gender">
            <el-radio label="male">男</el-radio>
            <el-radio label="female">女</el-radio>
            <el-radio label="other">其他</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="formData.phone" placeholder="请输入手机号" maxlength="11" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="formData.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="部门" prop="department">
          <el-input v-model="formData.department" placeholder="请输入部门" />
        </el-form-item>
        <el-form-item label="职务" prop="position">
          <el-input v-model="formData.position" placeholder="请输入职务" />
        </el-form-item>
        <el-form-item label="角色职务" prop="role_ids">
          <el-select
            v-model="formData.role_ids"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            placeholder="请选择角色（决定可用权限）"
            style="width: 100%"
          >
            <el-option
              v-for="role in roleOptions"
              :key="role.role_id"
              :label="`${role.role_name}${role.description ? `（${role.description}）` : ''}`"
              :value="role.role_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="密码" :prop="isEditing ? '' : 'password'">
          <el-input
            v-model="formData.password"
            type="password"
            placeholder="请输入密码（留空则不修改）"
            show-password
          />
          <div v-if="!isEditing" class="form-item-hint">{{ PASSWORD_POLICY_HINT }}</div>
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch v-model="formData.is_active" />
        </el-form-item>
        <el-form-item label="管理员" prop="is_admin">
          <el-switch v-model="formData.is_admin" />
        </el-form-item>
        <el-form-item label="首次改密" prop="must_change_password">
          <el-switch v-model="formData.must_change_password" />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showDialog = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog
      title="重置用户密码"
      v-model="showResetDialog"
      width="520px"
      @closed="resetPasswordForm.new_password = ''"
    >
      <el-form :model="resetPasswordForm" :rules="resetPasswordRules" ref="resetFormRef" label-width="120px">
        <el-form-item label="用户">
          <el-input v-model="resetPasswordForm.target_user_name" disabled />
        </el-form-item>
        <el-form-item label="新初始密码" prop="new_password">
          <el-input v-model="resetPasswordForm.new_password" type="password" show-password />
        </el-form-item>
        <el-form-item label="首次改密">
          <el-switch v-model="resetPasswordForm.must_change_password" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showResetDialog = false">取消</el-button>
        <el-button type="primary" @click="submitResetPassword">确认重置</el-button>
      </template>
    </el-dialog>

    <el-dialog
      title="批量开通用户"
      v-model="showBatchDialog"
      width="760px"
      @closed="batchResultText = ''"
    >
      <el-alert
        title="每行一个用户，格式：姓名,用户名,手机号,部门,岗位（后3项可留空）"
        type="info"
        :closable="false"
        style="margin-bottom: 12px"
      />
      <el-form label-width="130px">
        <el-form-item label="初始密码（统一）">
          <el-input v-model="batchInitialPassword" type="password" show-password />
          <div class="form-item-hint">{{ PASSWORD_POLICY_HINT }}</div>
        </el-form-item>
        <el-form-item label="首次改密">
          <el-switch v-model="batchMustChangePassword" />
        </el-form-item>
        <el-form-item label="用户列表文本">
          <el-input
            v-model="batchInputText"
            type="textarea"
            :rows="10"
            placeholder="示例：&#10;张三,zhangsan,13800138000,业务部,业务员&#10;李四,lisi,13900139000,采购部,采购助理"
          />
        </el-form-item>
      </el-form>
      <el-divider />
      <div v-if="batchResultText">
        <el-alert title="导入结果" type="success" :closable="false" style="margin-bottom: 8px" />
        <el-input v-model="batchResultText" type="textarea" :rows="8" readonly />
      </div>
      <template #footer>
        <el-button @click="showBatchDialog = false">关闭</el-button>
        <el-button @click="copyBatchResult" :disabled="!batchResultText">复制结果</el-button>
        <el-button type="primary" :loading="batchSubmitting" @click="submitBatchCreate">
          开始开通
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      title="登录设备管理"
      v-model="showSessionDialog"
      width="920px"
    >
      <div style="margin-bottom: 10px; display: flex; gap: 8px; align-items: center;">
        <el-tag type="info">{{ sessionTargetUserName || '-' }}</el-tag>
        <el-radio-group v-model="sessionFilterStatus" @change="loadUserSessions">
          <el-radio-button label="active">在线</el-radio-button>
          <el-radio-button label="all">全部</el-radio-button>
        </el-radio-group>
        <el-select v-model="sessionFilterDeviceType" style="width: 120px" @change="loadUserSessions">
          <el-option label="全部设备" value="" />
          <el-option label="电脑端" value="desktop" />
          <el-option label="手机端" value="mobile" />
        </el-select>
        <el-button @click="loadUserSessions">刷新</el-button>
      </div>

      <el-table v-loading="sessionLoading" :data="sessionTableData" size="small" border max-height="420">
        <el-table-column prop="device_type" label="设备类型" width="90">
          <template #default="{ row }">
            {{ row.device_type === 'mobile' ? '手机端' : '电脑端' }}
          </template>
        </el-table-column>
        <el-table-column prop="device_name" label="设备标识" min-width="190" show-overflow-tooltip />
        <el-table-column prop="client_ip" label="IP" width="120" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'">
              {{ row.status === 'active' ? '在线' : '离线' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="logged_in_at" label="登录时间" width="170">
          <template #default="{ row }">{{ formatDateTime(row.logged_in_at) }}</template>
        </el-table-column>
        <el-table-column prop="last_seen_at" label="最后活跃" width="170">
          <template #default="{ row }">{{ formatDateTime(row.last_seen_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'active'"
              size="small"
              type="danger"
              link
              @click="handleOfflineOneSession(row)"
            >
              下线
            </el-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <template #footer>
        <el-button @click="showSessionDialog = false">关闭</el-button>
        <el-button type="warning" @click="handleOfflineByDeviceType('desktop')">下线全部电脑端</el-button>
        <el-button type="warning" @click="handleOfflineByDeviceType('mobile')">下线全部手机端</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Plus } from '@element-plus/icons-vue'
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword, getUserLoginSessions, offlineUserLoginSessions } from '@/api/users'
import { getRoles, getUserRoles, setUserRoles } from '@/api/rbac'
import { PASSWORD_POLICY_HINT, validatePasswordClient } from '@/utils/passwordPolicy'

const loading = ref(false)
const searchKeyword = ref('')
const filterActive = ref('')
const showDialog = ref(false)
const showResetDialog = ref(false)
const showBatchDialog = ref(false)
const formRef = ref(null)
const resetFormRef = ref(null)
const isEditing = ref(false)
const batchSubmitting = ref(false)
const currentUserId = ref(JSON.parse(localStorage.getItem('user') || '{}')?.user_id)
const roleOptions = ref([])

const tableData = ref([])
const pagination = reactive({
  currentPage: 1,
  pageSize: 20,
  total: 0
})

const formData = reactive({
  username: '',
  full_name: '',
  gender: '',
  phone: '',
  email: '',
  department: '',
  position: '',
  role_ids: [],
  password: '',
  is_active: true,
  is_admin: false,
  must_change_password: true
})

const resetPasswordForm = reactive({
  target_user_id: null,
  target_user_name: '',
  target_username: '',
  new_password: '',
  must_change_password: true
})
const batchInputText = ref('')
const batchInitialPassword = ref('Welcome@123')
const batchMustChangePassword = ref(true)
const batchResultText = ref('')
const showSessionDialog = ref(false)
const sessionLoading = ref(false)
const sessionTableData = ref([])
const sessionTargetUserId = ref(null)
const sessionTargetUserName = ref('')
const sessionFilterStatus = ref('active')
const sessionFilterDeviceType = ref('')

const formRules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  full_name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
  phone: [
    { 
      validator: (rule, value, callback) => {
        if (!value || value.trim() === '') {
          callback()
        } else if (!/^1[3-9]\d{9}$/.test(value)) {
          callback(new Error('请输入正确的手机号'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  email: [
    { 
      validator: (rule, value, callback) => {
        if (!value || value.trim() === '') {
          callback()
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          callback(new Error('请输入正确的邮箱'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        const err = validatePasswordClient(value, formData.username)
        if (err) callback(new Error(err))
        else callback()
      },
      trigger: 'blur'
    }
  ]
}

const resetPasswordRules = {
  new_password: [
    { required: true, message: '请输入新初始密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        const err = validatePasswordClient(value, resetPasswordForm.target_username)
        if (err) callback(new Error(err))
        else callback()
      },
      trigger: 'blur'
    }
  ]
}

const dialogTitle = computed(() => isEditing.value ? '编辑用户' : '新增用户')

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      search: searchKeyword.value,
      is_active: filterActive.value
    }
    
    const response = await getUsers(params)
    
    if (response.success) {
      tableData.value = response.data || []
      pagination.total = response.pagination?.total || 0
      await Promise.all(
        tableData.value.map(async (u) => {
          try {
            const rr = await getUserRoles(u.user_id)
            u._roles = rr.success ? (rr.data || []) : []
          } catch (_) {
            u._roles = []
          }
        })
      )
    } else {
      ElMessage.error(response.message || '加载数据失败')
    }
  } catch (error) {
    ElMessage.error('加载数据失败: ' + (error.response?.data?.message || error.message))
  } finally {
    loading.value = false
  }
}

const loadRoles = async () => {
  try {
    const resp = await getRoles()
    if (resp.success) {
      roleOptions.value = resp.data || []
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '加载角色失败')
  }
}

// 搜索
const handleSearch = () => {
  pagination.currentPage = 1
  loadData()
}

// 新增
const handleAdd = async () => {
  await loadRoles()
  isEditing.value = false
  resetForm()
  showDialog.value = true
}

// 编辑
const handleEdit = async (row) => {
  await loadRoles()
  isEditing.value = true
  let roleIds = (row._roles || []).map(r => r.role_id)
  if (!roleIds.length) {
    try {
      const rr = await getUserRoles(row.user_id)
      if (rr.success) roleIds = (rr.data || []).map(r => r.role_id)
    } catch (_) {
      // Ignore role preload failure and allow editing basic user info.
    }
  }
  Object.assign(formData, {
    username: row.username,
    full_name: row.full_name,
    gender: row.gender || '',
    phone: row.phone || '',
    email: row.email || '',
    department: row.department || '',
    position: row.position || '',
    role_ids: roleIds,
    password: '',
    is_active: row.is_active,
    is_admin: row.is_admin || false,
    must_change_password: row.must_change_password || false
  })
  formData.user_id = row.user_id
  showDialog.value = true
}

// 切换状态
const handleToggleStatus = async (row) => {
  try {
    await updateUser(row.user_id, {
      ...row,
      is_active: !row.is_active
    })
    ElMessage.success('状态更新成功')
    loadData()
  } catch (error) {
    ElMessage.error('状态更新失败: ' + (error.response?.data?.message || error.message))
  }
}

// 删除
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除用户 "${row.full_name}" 吗？`,
      '警告',
      {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }
    )

    const response = await deleteUser(row.user_id)
    
    if (response.success) {
      ElMessage.success('删除成功')
      loadData()
    } else {
      ElMessage.error(response.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
    }
  }
}

// 管理员重置用户密码
const handleResetPassword = (row) => {
  resetPasswordForm.target_user_id = row.user_id
  resetPasswordForm.target_user_name = `${row.full_name} (${row.username})`
  resetPasswordForm.target_username = row.username || ''
  resetPasswordForm.new_password = ''
  resetPasswordForm.must_change_password = true
  showResetDialog.value = true
}

const submitResetPassword = async () => {
  if (!resetFormRef.value) return
  try {
    await resetFormRef.value.validate()
    const response = await resetUserPassword(resetPasswordForm.target_user_id, {
      new_password: resetPasswordForm.new_password,
      must_change_password: resetPasswordForm.must_change_password
    })
    if (response.success) {
      const text = buildNotifyText({
        full_name: resetPasswordForm.target_user_name,
        username: tableData.value.find(u => u.user_id === resetPasswordForm.target_user_id)?.username || '',
        password: resetPasswordForm.new_password
      })
      try {
        await copyText(text)
        ElMessage.success('密码已重置，通知内容已复制')
      } catch (_) {
        ElMessage.success('密码已重置，但通知内容复制失败')
      }
      showResetDialog.value = false
      loadData()
    } else {
      ElMessage.error(response.message || '重置密码失败')
    }
  } catch (error) {
    if (error !== false) {
      ElMessage.error(error.response?.data?.message || error.message || '重置密码失败')
    }
  }
}

const buildNotifyText = ({ full_name, username, password }) => {
  return `【系统账号开通通知】
姓名：${full_name || '-'}
登录用户名：${username || '-'}
初始密码：${password || '请联系管理员获取'}

登录时请输入“登录用户名”，不要输入姓名。
登录后请立即修改密码。
如收不到短信验证码，请联系管理员重置密码。`
}

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const copyNotifyTemplate = async (row) => {
  try {
    const text = buildNotifyText({
      full_name: row.full_name,
      username: row.username,
      password: '（请先点击“重置密码”后填写）'
    })
    await copyText(text)
    ElMessage.success('已复制通知模板')
  } catch (error) {
    ElMessage.error('复制失败，请手动复制')
  }
}

const openBatchDialog = () => {
  batchInputText.value = ''
  batchResultText.value = ''
  showBatchDialog.value = true
}

const normalizeUserLine = (line) => {
  const parts = line.split(',').map(v => (v || '').trim())
  return {
    full_name: parts[0] || '',
    username: parts[1] || '',
    phone: parts[2] || null,
    department: parts[3] || null,
    position: parts[4] || null
  }
}

const submitBatchCreate = async () => {
  const initialPassword = (batchInitialPassword.value || '').trim()
  const batchPwErr = validatePasswordClient(initialPassword)
  if (batchPwErr) {
    ElMessage.error(`初始密码不符合策略：${batchPwErr}`)
    return
  }

  const lines = batchInputText.value
    .split('\n')
    .map(v => v.trim())
    .filter(Boolean)

  if (!lines.length) {
    ElMessage.error('请先输入要开通的用户列表')
    return
  }

  batchSubmitting.value = true
  let success = 0
  const errors = []

  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1
    const row = normalizeUserLine(lines[i])
    if (!row.full_name || !row.username) {
      errors.push(`第${lineNo}行失败：姓名和用户名不能为空`)
      continue
    }
    try {
      const payload = {
        username: row.username,
        full_name: row.full_name,
        phone: row.phone,
        department: row.department,
        position: row.position,
        password: initialPassword,
        is_active: true,
        is_admin: false,
        must_change_password: batchMustChangePassword.value
      }
      const resp = await createUser(payload)
      if (resp.success) {
        success++
      } else {
        errors.push(`第${lineNo}行失败：${resp.message || '创建失败'}`)
      }
    } catch (err) {
      errors.push(`第${lineNo}行失败：${err.response?.data?.message || err.message || '创建失败'}`)
    }
  }

  batchSubmitting.value = false
  batchResultText.value = `总计：${lines.length}\n成功：${success}\n失败：${errors.length}\n` +
    (errors.length ? `\n失败详情：\n${errors.join('\n')}` : '\n全部创建成功。')

  if (success > 0) {
    ElMessage.success(`批量开通完成：成功 ${success} 人`)
    await loadData()
  } else {
    ElMessage.error('批量开通失败，请检查输入数据')
  }
}

const copyBatchResult = async () => {
  try {
    await copyText(batchResultText.value)
    ElMessage.success('导入结果已复制')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

const handleManageSessions = async (row) => {
  sessionTargetUserId.value = row.user_id
  sessionTargetUserName.value = `${row.full_name} (${row.username})`
  showSessionDialog.value = true
  await loadUserSessions()
}

const loadUserSessions = async () => {
  if (!sessionTargetUserId.value) return
  sessionLoading.value = true
  try {
    const resp = await getUserLoginSessions(sessionTargetUserId.value, {
      status: sessionFilterStatus.value,
      device_type: sessionFilterDeviceType.value,
      limit: 100
    })
    if (resp.success) {
      sessionTableData.value = resp.data || []
    } else {
      ElMessage.error(resp.message || '加载登录设备失败')
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '加载登录设备失败')
  } finally {
    sessionLoading.value = false
  }
}

const handleOfflineOneSession = async (row) => {
  try {
    await ElMessageBox.confirm('确定下线该设备会话吗？', '提示', { type: 'warning' })
    const resp = await offlineUserLoginSessions(sessionTargetUserId.value, {
      session_id: row.session_id,
      reason: 'admin_force_offline'
    })
    if (resp.success) {
      ElMessage.success('会话已下线')
      await loadUserSessions()
    } else {
      ElMessage.error(resp.message || '下线失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || error.message || '下线失败')
    }
  }
}

const handleOfflineByDeviceType = async (deviceType) => {
  try {
    const label = deviceType === 'mobile' ? '手机端' : '电脑端'
    await ElMessageBox.confirm(`确定下线该用户全部${label}会话吗？`, '提示', { type: 'warning' })
    const resp = await offlineUserLoginSessions(sessionTargetUserId.value, {
      device_type: deviceType,
      reason: 'admin_bulk_offline'
    })
    if (resp.success) {
      ElMessage.success(resp.message || '已执行下线')
      await loadUserSessions()
    } else {
      ElMessage.error(resp.message || '下线失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || error.message || '下线失败')
    }
  }
}

// 提交表单
const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()

    const submitData = { ...formData }
    
    // 如果是编辑且密码为空，不传密码字段
    if (isEditing.value && !submitData.password) {
      delete submitData.password
    }
    
    // 清理空字符串，转换为null或删除字段
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '') {
        // 对于可选字段，设置为null；对于必填字段，保留空字符串让后端验证
        if (['phone', 'email', 'department', 'position', 'gender'].includes(key)) {
          submitData[key] = null
        }
      }
    })

    let targetUserId = formData.user_id
    if (isEditing.value) {
      await updateUser(formData.user_id, submitData)
      await setUserRoles(formData.user_id, formData.role_ids || [])
      ElMessage.success('更新成功')
    } else {
      const createResp = await createUser(submitData)
      targetUserId = createResp?.data?.user_id
      if (targetUserId) {
        await setUserRoles(targetUserId, formData.role_ids || [])
      }
      ElMessage.success('创建成功')
    }

    showDialog.value = false
    loadData()
  } catch (error) {
    if (error !== false) {
      const errorMessage = error.response?.data?.message || error.message || '操作失败'
      ElMessage.error(errorMessage)
      console.error('提交失败:', error)
    }
  }
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    username: '',
    full_name: '',
    gender: '',
    phone: '',
    email: '',
    department: '',
    position: '',
    role_ids: [],
    password: '',
    is_active: true,
    is_admin: false,
    must_change_password: true
  })
  isEditing.value = false
}

// 格式化日期时间
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

onMounted(() => {
  loadRoles()
  loadData()
})

defineExpose({ loadRoles })
</script>

<style scoped>
.user-management {
  /* 主区 .main-content 已有 padding，这里不再叠一层，避免视觉拥挤 */
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
}

.filter-section {
  margin-bottom: 20px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-item-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.45;
  max-width: 520px;
}
</style>

